/**
 * Admin Session Settings Page
 * Allows admins to configure global idle timeout settings
 * V1 REQUIREMENT: All timestamps displayed in America/New_York (ET)
 */
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Clock, Save, RefreshCw, Shield, Info } from 'lucide-react';
import { formatDateTimeShortET } from '../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const AdminSessionSettings = () => {
  const [timeoutMinutes, setTimeoutMinutes] = useState(15);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalValue, setOriginalValue] = useState(15);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/settings/idle-timeout`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch session settings');
      }

      const data = await response.json();
      setTimeoutMinutes(data.timeout_minutes);
      setOriginalValue(data.timeout_minutes);
      setLastUpdated(data.updated_at);
      setUpdatedBy(data.updated_by);
      setHasChanges(false);
    } catch (error) {
      console.error('Error fetching session settings:', error);
      toast.error('Failed to load session settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSliderChange = (value) => {
    const newValue = value[0];
    setTimeoutMinutes(newValue);
    setHasChanges(newValue !== originalValue);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/settings/idle-timeout`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ timeout_minutes: timeoutMinutes })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save settings');
      }

      const data = await response.json();
      setOriginalValue(data.timeout_minutes);
      setLastUpdated(data.updated_at);
      setUpdatedBy(data.updated_by);
      setHasChanges(false);
      
      toast.success(`Idle timeout updated to ${timeoutMinutes} minutes`, {
        description: 'Changes take effect immediately for all users'
      });
    } catch (error) {
      console.error('Error saving session settings:', error);
      toast.error(error.message || 'Failed to save session settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setTimeoutMinutes(originalValue);
    setHasChanges(false);
  };

  // V1 REQUIREMENT: Use centralized ET formatter
  const formatLastUpdated = (dateString) => {
    if (!dateString) return 'Never';
    return formatDateTimeShortET(dateString);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-blue-500/20 rounded-lg">
          <Clock className="h-6 w-6 text-blue-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Session Settings</h1>
          <p className="text-slate-400">Configure global idle timeout for all users</p>
        </div>
      </div>

      {/* Main Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-blue-500" />
            Idle Timeout
          </CardTitle>
          <CardDescription>
            Users will be automatically logged out after this period of inactivity.
            This setting applies globally to all users.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Timeout Slider */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-base font-medium">Timeout Duration</Label>
              <span className="text-2xl font-bold text-blue-600">
                {timeoutMinutes} minutes
              </span>
            </div>
            
            <Slider
              value={[timeoutMinutes]}
              onValueChange={handleSliderChange}
              min={10}
              max={60}
              step={5}
              className="w-full"
            />
            
            <div className="flex justify-between text-sm text-slate-500">
              <span>10 min (strict)</span>
              <span>35 min (default)</span>
              <span>60 min (relaxed)</span>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
            <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">How it works:</p>
              <ul className="list-disc list-inside space-y-1 text-blue-700">
                <li>Users see a warning 2 minutes before timeout</li>
                <li>Only real activity (mouse, keyboard, scroll) resets the timer</li>
                <li>Background API polling does NOT extend sessions</li>
                <li>Changes take effect immediately for new requests</li>
              </ul>
            </div>
          </div>

          {/* Last Updated Info */}
          {lastUpdated && (
            <div className="text-sm text-slate-500 border-t pt-4">
              Last updated: {formatLastUpdated(lastUpdated)}
              {updatedBy && ` by ${updatedBy}`}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="flex-1"
            >
              {saving ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
            
            {hasChanges && (
              <Button
                variant="outline"
                onClick={handleReset}
                disabled={saving}
              >
                Reset
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Security Note */}
      <Card className="bg-slate-50">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <Shield className="h-5 w-5 text-slate-400 flex-shrink-0" />
            <div className="text-sm text-slate-600">
              <p className="font-medium text-slate-700 mb-1">Security Note</p>
              <p>
                Shorter timeout values provide better security but may impact user experience.
                For compliance-sensitive environments, we recommend 15-20 minutes.
                All timeout changes are logged for audit purposes.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSessionSettings;
